import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/lib/firebase';
import { Product, Laboratory, ProductCategory } from '@/types/product';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const categories: { value: ProductCategory; label: string }[] = [
  { value: 'medicament', label: 'Médicament' },
  { value: 'complement', label: 'Complément alimentaire' },
  { value: 'beaute', label: 'Produit de beauté' },
  { value: 'materiel', label: 'Matériel médical' },
];

const AdminLabProducts = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const [laboratory, setLaboratory] = useState<Laboratory | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [form, setForm] = useState<Omit<Product, 'id'|'laboratoryId'|'createdAt'|'updatedAt'>>({
    name: '',
    description: '',
    category: 'medicament',
    images: [],
    price: '',
    dosage: '',
    presentation: '',
    indication: '',
    composition: '',
    status: 'active',
  });
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (labId) {
      fetchLaboratory();
      fetchProducts();
    }
  }, [labId]);

  const fetchLaboratory = async () => {
    const docRef = doc(db, COLLECTIONS.LABORATORIES, labId!);
    const docSnap = await getDocs(query(collection(db, COLLECTIONS.LABORATORIES), where('__name__', '==', labId)));
    if (!docSnap.empty) {
      setLaboratory({ id: labId!, ...docSnap.docs[0].data() } as Laboratory);
    }
  };

  const fetchProducts = async () => {
    const q = query(collection(db, COLLECTIONS.PRODUCTS), where('laboratoryId', '==', labId));
    const snapshot = await getDocs(q);
    const prods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Product[];
    setProducts(prods);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (value: ProductCategory) => {
    setForm({ ...form, category: value });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    let imageUrl = form.images[0] || '';
    if (imageFile) {
      // Upload image to Firebase Storage
      const storageRef = (await import('firebase/storage')).ref;
      const { uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = (await import('@/lib/firebase')).storage;
      const fileRef = storageRef(storage, `products/${Date.now()}_${imageFile.name}`);
      const snapshot = await uploadBytes(fileRef, imageFile);
      imageUrl = await getDownloadURL(snapshot.ref);
    }
    const productData = {
      ...form,
      images: [imageUrl],
      laboratoryId: labId!,
      status: form.status,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (selectedProduct) {
      // Update
      await updateDoc(doc(db, COLLECTIONS.PRODUCTS, selectedProduct), productData);
    } else {
      // Add
      await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
    }
    setForm({ name: '', description: '', category: 'medicament', images: [], price: '', dosage: '', presentation: '', indication: '', composition: '', status: 'active' });
    setImageFile(null);
    setSelectedProduct(null);
    await fetchProducts();
    setLoading(false);
  };

  const handleEdit = (product: Product) => {
    setForm({
      name: product.name,
      description: product.description,
      category: product.category,
      images: product.images,
      price: product.price || '',
      dosage: product.dosage || '',
      presentation: product.presentation || '',
      indication: product.indication || '',
      composition: product.composition || '',
      status: product.status,
    });
    setSelectedProduct(product.id);
  };

  const handleDelete = async (id: string) => {
    await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, id));
    await fetchProducts();
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <Button variant="ghost" className="mb-6" onClick={() => navigate('/admin/laboratories')}>← Retour aux laboratoires</Button>
      {laboratory && (
        <div className="flex items-center gap-4 mb-8">
          <img src={laboratory.logo} alt={laboratory.name} className="h-12 w-12 object-contain rounded bg-gray-100" />
          <h1 className="text-2xl font-bold">{laboratory.name}</h1>
        </div>
      )}
      <form onSubmit={handleSubmit} className="mb-8 space-y-4 bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nom du produit</Label>
            <Input name="name" value={form.name} onChange={handleInputChange} required />
          </div>
          <div>
            <Label>Type d'usage</Label>
            <Select value={form.category} onValueChange={handleCategoryChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <textarea name="description" value={form.description} onChange={handleInputChange} className="w-full border rounded p-2" required />
        </div>
        <div>
          <Label>Image</Label>
          <Input type="file" accept="image/*" onChange={handleImageChange} />
          {form.images[0] && !imageFile && (
            <img src={form.images[0]} alt="produit" className="h-16 mt-2" />
          )}
          {imageFile && (
            <span className="text-sm text-gray-500">{imageFile.name}</span>
          )}
        </div>
        <div className="flex gap-4">
          <Button type="submit" disabled={loading}>{selectedProduct ? 'Mettre à jour' : 'Ajouter'}</Button>
          {selectedProduct && (
            <Button type="button" variant="outline" onClick={() => { setForm({ name: '', description: '', category: 'medicament', images: [], price: '', dosage: '', presentation: '', indication: '', composition: '', status: 'active' }); setSelectedProduct(null); setImageFile(null); }}>Annuler</Button>
          )}
        </div>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map(product => (
          <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="aspect-w-16 aspect-h-9">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            <CardHeader className="p-4">
              <CardTitle className="text-lg">{product.name}</CardTitle>
              <div className="text-sm text-blue-600 mb-2">{categories.find(cat => cat.value === product.category)?.label}</div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {product.description}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(product)}>Modifier</Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(product.id)}>Supprimer</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminLabProducts; 